import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import api from "../../api/client";
import "./EventsCalendar.css";

/* ── helpers ── */
const pad = (n) => String(n).padStart(2, "0");
const isoDate = (d) =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const fmt = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
};
const monthLabel = (d) =>
  d.toLocaleDateString(undefined, { month: "long", year: "numeric" });
const currency = (v) => {
  const n = Number(v);
  if (!n && n !== 0) return "$0.00";
  return "$" + n.toFixed(2);
};

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function startOfMonth(d) {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}
function endOfMonth(d) {
  return new Date(d.getFullYear(), d.getMonth() + 1, 0);
}
function addMonths(d, n) {
  return new Date(d.getFullYear(), d.getMonth() + n, 1);
}
function startOfWeek(d) {
  const r = new Date(d);
  r.setDate(r.getDate() - r.getDay());
  return r;
}
function addDays(d, n) {
  const r = new Date(d);
  r.setDate(r.getDate() + n);
  return r;
}
function daysBetween(a, b) {
  const msA = new Date(a + "T00:00:00").getTime();
  const msB = new Date(b + "T00:00:00").getTime();
  return Math.round((msB - msA) / 86400000);
}

const STATUS_COLORS = {
  hold: "var(--warning, #f59e0b)",     // tentative date-hold — amber, distinct from confirmed
  scheduled: "var(--accent)",
  confirmed: "var(--success, #22c55e)",
  completed: "var(--text-sub)",
  canceled: "var(--danger, #ef4444)",
  no_show: "var(--warning, #f59e0b)",
};

/* ──────────────────────────── Component ──────────────────────────── */
export default function EventsCalendar({ initialView, initialDate } = {}) {
  const navigate = useNavigate();

  /* ── core state ── */
  // view and the reference day can be seeded from the URL (/calendar/day/YYYY-MM-DD,
  // /calendar/week, /calendar/month). range derives the month/week/day window from
  // `cursor`, so cursor is just any day inside the period being shown.
  const seedDate = initialDate ? new Date(initialDate + "T12:00:00") : new Date();
  const [view, setView] = useState(
    ["month", "week", "day"].includes(initialView) ? initialView : "month"
  );
  const [cursor, setCursor] = useState(() => seedDate);
  const [bookings, setBookings] = useState([]);
  const [blocked, setBlocked] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDay, setSelectedDay] = useState(
    initialView === "day" && initialDate ? initialDate : null
  );
  const [detailBooking, setDetailBooking] = useState(null);

  /* ── block modal state ── */
  const [blockPrompt, setBlockPrompt] = useState(null);
  const [blockReason, setBlockReason] = useState("");
  const [blockEndDate, setBlockEndDate] = useState("");

  /* ── balances state ── */
  const [balances, setBalances] = useState([]);
  const [balancesOpen, setBalancesOpen] = useState(false);
  const [balancesLoading, setBalancesLoading] = useState(false);

  /* ── logistics editor state ── */
  const [logisticsEditing, setLogisticsEditing] = useState(false);
  const [logisticsForm, setLogisticsForm] = useState(null);
  const [logisticsSaving, setLogisticsSaving] = useState(false);

  /* ── guest list state ── */
  const [guests, setGuests] = useState([]);
  const [guestSummary, setGuestSummary] = useState(null);
  const [guestsLoading, setGuestsLoading] = useState(false);
  const [showAddGuest, setShowAddGuest] = useState(false);
  const [newGuestName, setNewGuestName] = useState("");
  const [newGuestPhone, setNewGuestPhone] = useState("");
  const [newGuestEmail, setNewGuestEmail] = useState("");
  const [showBulkAdd, setShowBulkAdd] = useState(false);
  const [bulkText, setBulkText] = useState("");
  const [waiversRequired, setWaiversRequired] = useState(false);
  const [waiverModal, setWaiverModal] = useState(null);

  /* ── data range ── */
  const range = useMemo(() => {
    if (view === "month") {
      const s = startOfMonth(cursor);
      const e = endOfMonth(cursor);
      const gridStart = startOfWeek(s);
      const gridEnd = addDays(startOfWeek(addDays(e, 6)), 6);
      return {
        apiStart: isoDate(s),
        apiEnd: isoDate(e),
        gridStart,
        gridEnd,
        month: s.getMonth(),
        year: s.getFullYear(),
      };
    }
    if (view === "day") {
      // Fetch a little padding around the day so a booking whose setup or
      // teardown buffer spills into the day is still returned and indexed.
      const s = addDays(cursor, -3);
      const e = addDays(cursor, 3);
      return { apiStart: isoDate(s), apiEnd: isoDate(e), gridStart: cursor, gridEnd: cursor };
    }
    const s = startOfWeek(cursor);
    const e = addDays(s, 6);
    return { apiStart: isoDate(s), apiEnd: isoDate(e), gridStart: s, gridEnd: e };
  }, [view, cursor]);

  /* ── fetch calendar data ── */
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/scheduling/events-calendar/", {
        params: { start: range.apiStart, end: range.apiEnd },
      });
      setBookings(data?.bookings || []);
      setBlocked(data?.blocked_dates || []);
    } catch {
      toast.error("Failed to load calendar");
      setBookings([]);
      setBlocked([]);
    } finally {
      setLoading(false);
    }
  }, [range.apiStart, range.apiEnd]);

  useEffect(() => {
    load();
  }, [load]);

  /* ── fetch balances ── */
  const loadBalances = useCallback(async () => {
    setBalancesLoading(true);
    try {
      const { data } = await api.get("/scheduling/balances-due/");
      setBalances(data?.balances || []);
    } catch {
      toast.error("Failed to load balances");
      setBalances([]);
    } finally {
      setBalancesLoading(false);
    }
  }, []);

  useEffect(() => {
    loadBalances();
  }, [loadBalances]);

  /* ── land on a month that has events ──
     The calendar opens on the current month. When that month is empty but the
     tenant has events in another month, the operator saw a blank calendar and
     read it as "no events" (the t27 case: events are in July, the calendar
     opened on October). Once, on first load, if the current month came back
     empty and we weren't deep-linked to a date, probe the events-calendar in
     90-day windows outward from today (the endpoint caps a request at 93 days)
     and jump the cursor to the month of the nearest real booking — nearest
     window first, upcoming preferred over past. Probing the calendar itself,
     not the balances feed, so we never land on a month the grid can't fill. */
  const didAutoJumpRef = useRef(false);
  useEffect(() => {
    if (didAutoJumpRef.current) return;
    if (initialDate || view !== "month") { didAutoJumpRef.current = true; return; }
    if (loading) return; // wait for the current month's load to settle
    if (bookings.length > 0) { didAutoJumpRef.current = true; return; } // current month has events
    didAutoJumpRef.current = true; // probe once

    let cancelled = false;
    (async () => {
      const today = new Date();
      // Windows ordered by nearness to today: forward0, back0, forward1, back1…
      const windows = [];
      for (let i = 0; i < 4; i++) {
        windows.push([addDays(today, i * 90 + 1), addDays(today, (i + 1) * 90), "f"]);
        windows.push([addDays(today, -((i + 1) * 90) + 1), addDays(today, -(i * 90)), "b"]);
      }
      for (const [s, e, dir] of windows) {
        if (cancelled) return;
        try {
          const { data } = await api.get("/scheduling/events-calendar/", {
            params: { start: isoDate(s), end: isoDate(e) },
          });
          const dates = (data?.bookings || []).map((b) => b.date).filter(Boolean).sort();
          if (dates.length) {
            // forward window: earliest upcoming; backward window: latest (nearest to today)
            const target = dir === "f" ? dates[0] : dates[dates.length - 1];
            if (!cancelled) setCursor(new Date(target + "T12:00:00"));
            return;
          }
        } catch {
          /* a failed probe window is skipped; the operator can still navigate */
        }
      }
    })();
    return () => { cancelled = true; };
  }, [loading, bookings, initialDate, view]);

  /* ── index bookings by date (including buffer days) ── */
  const byDate = useMemo(() => {
    const map = {};
    for (const b of bookings) {
      (map[b.date] ||= []).push({ ...b, _bufferType: null });

      if (b.setup_start) {
        const setupDate = b.setup_start.slice(0, 10);
        if (setupDate !== b.date) {
          let d = new Date(setupDate + "T00:00:00");
          const eventDate = new Date(b.date + "T00:00:00");
          while (d < eventDate) {
            const iso = isoDate(d);
            if (iso !== b.date) {
              (map[iso] ||= []).push({ ...b, _bufferType: "setup" });
            }
            d = addDays(d, 1);
          }
        }
      }

      if (b.teardown_end) {
        const teardownDate = b.teardown_end.slice(0, 10);
        if (teardownDate !== b.date) {
          let d = addDays(new Date(b.date + "T00:00:00"), 1);
          const end = new Date(teardownDate + "T00:00:00");
          while (d <= end) {
            const iso = isoDate(d);
            (map[iso] ||= []).push({ ...b, _bufferType: "teardown" });
            d = addDays(d, 1);
          }
        }
      }
    }
    return map;
  }, [bookings]);

  /* ── index blocked dates ── */
  const blockedSet = useMemo(() => {
    const s = new Set();
    for (const b of blocked) {
      let d = new Date(b.start + "T00:00:00");
      const end = new Date(b.end + "T00:00:00");
      while (d <= end) {
        s.add(isoDate(d));
        d = addDays(d, 1);
      }
    }
    return s;
  }, [blocked]);

  const blockedReasons = useMemo(() => {
    const m = {};
    for (const b of blocked) {
      let d = new Date(b.start + "T00:00:00");
      const end = new Date(b.end + "T00:00:00");
      while (d <= end) {
        m[isoDate(d)] = { reason: b.reason, id: b.id };
        d = addDays(d, 1);
      }
    }
    return m;
  }, [blocked]);

  /* ── navigation ── */
  const step = view === "day" ? 1 : 7;
  const prev = () =>
    setCursor((c) => (view === "month" ? addMonths(c, -1) : addDays(c, -step)));
  const next = () =>
    setCursor((c) => (view === "month" ? addMonths(c, 1) : addDays(c, step)));
  const goToday = () => {
    const now = new Date();
    setCursor(now);
    setSelectedDay(isoDate(now));
  };

  /* ── block / unblock ── */
  const openBlockPrompt = (date) => {
    setBlockPrompt({ date });
    setBlockReason("");
    setBlockEndDate("");
  };

  const handleBlock = async () => {
    if (!blockPrompt) return;
    try {
      const startDt = blockPrompt.date;
      const endDt = blockEndDate || startDt;
      await api.post("/scheduling/blackouts/", {
        start_at: `${startDt}T00:00:00`,
        end_at: `${endDt}T23:59:59`,
        reason: blockReason || "Blocked",
      });
      toast.success(
        startDt === endDt ? "Date blocked" : "Date range blocked"
      );
      setBlockPrompt(null);
      setBlockReason("");
      setBlockEndDate("");
      load();
    } catch {
      toast.error("Failed to block date");
    }
  };

  const handleUnblock = async (blockId) => {
    try {
      await api.delete(`/scheduling/blackouts/${blockId}/`);
      toast.success("Date unblocked");
      load();
    } catch {
      toast.error("Failed to unblock");
    }
  };

  /* ── mark paid ── */
  const markDepositPaid = async (quoteId) => {
    try {
      await api.post(`/scheduling/mark-deposit-paid/${quoteId}/`);
      toast.success("Deposit marked paid");
      load();
      loadBalances();
      if (detailBooking && detailBooking.quote_id === quoteId) {
        setDetailBooking((prev) =>
          prev ? { ...prev, deposit_paid: true } : prev
        );
      }
    } catch {
      toast.error("Failed to mark deposit paid");
    }
  };

  const markBalancePaid = async (quoteId) => {
    try {
      await api.post(`/scheduling/mark-balance-paid/${quoteId}/`);
      toast.success("Balance marked paid");
      load();
      loadBalances();
      if (detailBooking && detailBooking.quote_id === quoteId) {
        setDetailBooking((prev) =>
          prev ? { ...prev, balance_paid: true } : prev
        );
      }
    } catch {
      toast.error("Failed to mark balance paid");
    }
  };

  /* ── logistics ── */
  const openLogisticsEditor = (booking) => {
    const lg = booking.logistics || {};
    setLogisticsForm({
      estimated_headcount: lg.estimated_headcount || "",
      final_headcount: lg.final_headcount || "",
      setup_notes: lg.setup_notes || "",
      vendors: lg.vendors && lg.vendors.length > 0
        ? lg.vendors.map((v) => ({ ...v }))
        : [],
      day_of_contact: lg.day_of_contact
        ? { ...lg.day_of_contact }
        : { name: "", phone: "" },
      notes: lg.notes || "",
    });
    setLogisticsEditing(true);
  };

  const handleLogisticsChange = (field, value) => {
    setLogisticsForm((prev) => (prev ? { ...prev, [field]: value } : prev));
  };

  const handleVendorChange = (index, field, value) => {
    setLogisticsForm((prev) => {
      if (!prev) return prev;
      const vendors = [...prev.vendors];
      vendors[index] = { ...vendors[index], [field]: value };
      return { ...prev, vendors };
    });
  };

  const addVendor = () => {
    setLogisticsForm((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        vendors: [...prev.vendors, { name: "", arrival_time: "", contact: "" }],
      };
    });
  };

  const removeVendor = (index) => {
    setLogisticsForm((prev) => {
      if (!prev) return prev;
      const vendors = prev.vendors.filter((_, i) => i !== index);
      return { ...prev, vendors };
    });
  };

  const handleDayOfContactChange = (field, value) => {
    setLogisticsForm((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        day_of_contact: { ...prev.day_of_contact, [field]: value },
      };
    });
  };

  const saveLogistics = async () => {
    if (!detailBooking || !logisticsForm) return;
    setLogisticsSaving(true);
    try {
      const payload = {
        logistics: {
          estimated_headcount: logisticsForm.estimated_headcount
            ? Number(logisticsForm.estimated_headcount)
            : null,
          final_headcount: logisticsForm.final_headcount
            ? Number(logisticsForm.final_headcount)
            : null,
          setup_notes: logisticsForm.setup_notes,
          vendors: logisticsForm.vendors.filter(
            (v) => v.name || v.arrival_time || v.contact
          ),
          day_of_contact: logisticsForm.day_of_contact,
          notes: logisticsForm.notes,
        },
      };
      await api.patch(
        `/scheduling/event-logistics/${detailBooking.id}/`,
        payload
      );
      toast.success("Logistics saved");
      setDetailBooking((prev) =>
        prev ? { ...prev, logistics: payload.logistics } : prev
      );
      setLogisticsEditing(false);
      load();
    } catch {
      toast.error("Failed to save logistics");
    } finally {
      setLogisticsSaving(false);
    }
  };

  /* ── guest list helpers ── */
  const loadGuests = useCallback(async (appointmentId) => {
    if (!appointmentId) return;
    setGuestsLoading(true);
    try {
      const { data } = await api.get("/events/guests/", {
        params: { appointment: appointmentId },
      });
      setGuests(data?.guests || []);
      setGuestSummary(data?.summary || null);
      const hasRequired = (data?.guests || []).some(
        (g) => g.waiver_status !== "not_required"
      );
      setWaiversRequired(hasRequired);
    } catch {
      setGuests([]);
      setGuestSummary(null);
    } finally {
      setGuestsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (detailBooking?.id) {
      loadGuests(detailBooking.id);
    } else {
      setGuests([]);
      setGuestSummary(null);
    }
  }, [detailBooking?.id, loadGuests]);

  const RSVP_ORDER = ["invited", "confirmed", "attended", "declined"];

  const cycleRsvp = async (guest) => {
    const idx = RSVP_ORDER.indexOf(guest.rsvp_status);
    const next = RSVP_ORDER[(idx + 1) % RSVP_ORDER.length];
    try {
      await api.patch(`/events/guests/${guest.id}/`, { rsvp_status: next });
      setGuests((prev) =>
        prev.map((g) => (g.id === guest.id ? { ...g, rsvp_status: next } : g))
      );
      loadGuests(detailBooking?.id);
    } catch {
      toast.error("Failed to update RSVP");
    }
  };

  const addGuest = async () => {
    if (!newGuestName.trim()) {
      toast.error("Guest name is required");
      return;
    }
    try {
      await api.post("/events/guests/", {
        appointment: detailBooking.id,
        name: newGuestName.trim(),
        phone: newGuestPhone.trim() || undefined,
        email: newGuestEmail.trim() || undefined,
      });
      toast.success("Guest added");
      setNewGuestName("");
      setNewGuestPhone("");
      setNewGuestEmail("");
      setShowAddGuest(false);
      loadGuests(detailBooking.id);
    } catch {
      toast.error("Failed to add guest");
    }
  };

  const bulkAddGuests = async () => {
    if (!bulkText.trim()) return;
    try {
      await api.post("/events/guests/bulk-add/", {
        appointment: detailBooking.id,
        lines: bulkText.trim(),
      });
      toast.success("Guests added");
      setBulkText("");
      setShowBulkAdd(false);
      loadGuests(detailBooking.id);
    } catch {
      toast.error("Failed to add guests");
    }
  };

  const deleteGuest = async (guestId) => {
    try {
      await api.delete(`/events/guests/${guestId}/`);
      setGuests((prev) => prev.filter((g) => g.id !== guestId));
      loadGuests(detailBooking?.id);
    } catch {
      toast.error("Failed to remove guest");
    }
  };

  const promoteGuest = async (guest) => {
    try {
      const { data } = await api.post(`/events/guests/${guest.id}/promote/`);
      toast.success(`Promoted to lead: ${data.lead_name || "Lead"}`);
      setGuests((prev) =>
        prev.map((g) =>
          g.id === guest.id ? { ...g, promoted_lead_id: data.lead_id } : g
        )
      );
    } catch {
      toast.error("Failed to promote guest");
    }
  };

  const sendWaiver = async (guest) => {
    try {
      await api.post(`/events/guests/${guest.id}/send-waiver/`);
      toast.success("Waiver email sent");
      setWaiverModal(null);
      loadGuests(detailBooking?.id);
    } catch {
      toast.error("Failed to send waiver");
    }
  };

  const toggleWaivers = async () => {
    try {
      const newVal = !waiversRequired;
      await api.post("/events/guests/toggle-waivers/", {
        appointment: detailBooking.id,
        required: newVal,
      });
      setWaiversRequired(newVal);
      toast.success(newVal ? "Waivers now required" : "Waivers no longer required");
      loadGuests(detailBooking.id);
    } catch {
      toast.error("Failed to toggle waivers");
    }
  };

  const copyWaiverLink = (token) => {
    const url = `https://os.abon.ai/events/waiver/${token}/`;
    navigator.clipboard.writeText(url).then(
      () => toast.success("Waiver link copied"),
      () => toast.error("Failed to copy link")
    );
  };

  /* ── build grid days ── */
  const gridDays = useMemo(() => {
    const days = [];
    let d = new Date(range.gridStart);
    const limit = view === "month" ? 42 : 7;
    for (let i = 0; i < limit; i++) {
      days.push(new Date(d));
      d = addDays(d, 1);
    }
    return days;
  }, [range.gridStart, view]);

  /* ── day detail data ── */
  const dayBookings = selectedDay ? byDate[selectedDay] || [] : [];
  const dayBlocked = selectedDay ? blockedSet.has(selectedDay) : false;
  const dayBlockInfo = selectedDay ? blockedReasons[selectedDay] : null;

  /* ── balances summary ── */
  const unpaidCount = useMemo(() => {
    return balances.filter((b) => !b.deposit_paid || !b.balance_paid).length;
  }, [balances]);

  /* ──────── RENDER ──────── */
  const todayStr = isoDate(new Date());

  return (
    <div className="ec-root">
      {/* ── Balances bar ── */}
      <div className="ec-balances-bar">
        <button
          className="ec-balances-toggle"
          onClick={() => {
            setBalancesOpen((o) => !o);
            if (!balancesOpen) loadBalances();
          }}
        >
          <span className="ec-balances-toggle-label">
            Upcoming Balances
            {unpaidCount > 0 && (
              <span className="ec-balances-badge">{unpaidCount}</span>
            )}
          </span>
          <span
            className={`ec-balances-chevron ${balancesOpen ? "ec-balances-chevron-open" : ""}`}
          >
            &#x25BE;
          </span>
        </button>

        {balancesOpen && (
          <div className="ec-balances-content">
            {balancesLoading && (
              <p className="ec-empty">Loading balances...</p>
            )}
            {!balancesLoading && balances.length === 0 && (
              <p className="ec-empty">No upcoming balances.</p>
            )}
            {!balancesLoading && balances.length > 0 && (
              <div className="ec-balances-list">
                {balances.map((b, i) => {
                  const daysUntil = daysBetween(todayStr, b.event_date || b.date);
                  return (
                    <div key={b.quote_id || i} className="ec-balance-row">
                      <div className="ec-balance-info">
                        <strong>{b.customer_name}</strong>
                        <span className="ec-balance-date">
                          {b.event_date || b.date}
                          {daysUntil >= 0 && (
                            <span className="ec-balance-days">
                              {daysUntil === 0
                                ? " (today)"
                                : daysUntil === 1
                                  ? " (tomorrow)"
                                  : ` (${daysUntil} days)`}
                            </span>
                          )}
                        </span>
                      </div>
                      <div className="ec-balance-amounts">
                        <span className="ec-balance-item">
                          Deposit {currency(b.deposit_amount)}
                          {b.deposit_paid ? (
                            <span className="ec-paid-tag">Paid</span>
                          ) : (
                            <button
                              className="ec-btn-sm primary"
                              onClick={() => markDepositPaid(b.quote_id)}
                            >
                              Mark Paid
                            </button>
                          )}
                        </span>
                        <span className="ec-balance-item">
                          Balance {currency(b.balance_due)}
                          {b.balance_paid ? (
                            <span className="ec-paid-tag">Paid</span>
                          ) : (
                            <button
                              className="ec-btn-sm primary"
                              onClick={() => markBalancePaid(b.quote_id)}
                            >
                              Mark Paid
                            </button>
                          )}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Header ── */}
      <div className="ec-header">
        <div className="ec-header-left">
          <h2>Events Calendar</h2>
          <span className="ec-subtitle">
            {view === "month"
              ? monthLabel(cursor)
              : view === "day"
                ? new Date(isoDate(cursor) + "T12:00:00").toLocaleDateString(
                    undefined,
                    { weekday: "long", month: "long", day: "numeric", year: "numeric" }
                  )
                : `Week of ${range.apiStart}`}
          </span>
        </div>
        <div className="ec-header-actions">
          <button
            className="ec-btn primary"
            onClick={() =>
              navigate("/quotes/create", {
                state: selectedDay ? { eventDate: selectedDay } : undefined,
              })
            }
          >
            + New Booking
          </button>
          <button className="ec-btn secondary" onClick={goToday}>
            Today
          </button>
          <button className="ec-btn secondary" onClick={prev}>
            &lsaquo;
          </button>
          <button className="ec-btn secondary" onClick={next}>
            &rsaquo;
          </button>
          <div className="ec-toggle">
            <button
              className={`ec-toggle-btn ${view === "month" ? "active" : ""}`}
              onClick={() => setView("month")}
            >
              Month
            </button>
            <button
              className={`ec-toggle-btn ${view === "week" ? "active" : ""}`}
              onClick={() => setView("week")}
            >
              Week
            </button>
            <button
              className={`ec-toggle-btn ${view === "day" ? "active" : ""}`}
              onClick={() => {
                setSelectedDay(isoDate(cursor));
                setView("day");
              }}
            >
              Day
            </button>
          </div>
        </div>
      </div>

      {/* ── Body ── */}
      <div className={`ec-body ${view === "day" ? "ec-body-day" : ""}`}>
        {/* Day view — a single-day agenda: bookings (with setup/teardown buffers)
            and the blocked state for exactly one day. Driven off `cursor`. */}
        {view === "day" && (() => {
          const dayIso = isoDate(cursor);
          const items = byDate[dayIso] || [];
          const isBlk = blockedSet.has(dayIso);
          const blkInfo = blockedReasons[dayIso];
          return (
            <div className="ec-day-agenda">
              {isBlk ? (
                <div className="ec-panel-blocked">
                  <span>
                    Date is blocked{blkInfo?.reason ? `: ${blkInfo.reason}` : ""}
                  </span>
                  <button
                    className="ec-btn-sm danger"
                    onClick={() => blkInfo && handleUnblock(blkInfo.id)}
                  >
                    Unblock
                  </button>
                </div>
              ) : (
                <button
                  className="ec-btn-sm secondary"
                  onClick={() => openBlockPrompt(dayIso)}
                  style={{ marginBottom: 12 }}
                >
                  Block date(s)
                </button>
              )}

              {items.length === 0 ? (
                <p className="ec-empty">No events booked for this day.</p>
              ) : (
                <div className="ec-day-list">
                  {items.map((b, idx) => (
                    <div
                      key={`${b.id}-${b._bufferType || "main"}-${idx}`}
                      className={`ec-day-card ${b._bufferType ? "ec-day-card-buffer" : ""}`}
                      style={{
                        borderLeftColor: b._bufferType
                          ? "var(--text-sub)"
                          : STATUS_COLORS[b.status] || "var(--accent)",
                      }}
                      onClick={() => {
                        setDetailBooking(b);
                        setLogisticsEditing(false);
                      }}
                    >
                      <div className="ec-day-card-main">
                        <strong>
                          {b._bufferType
                            ? `${b._bufferType === "setup" ? "Setup" : "Teardown"} — ${b.customer_name}`
                            : b.customer_name}
                        </strong>
                        <div className="ec-day-card-meta">
                          {!b._bufferType && (
                            <span>
                              {fmt(b.start_time)}
                              {b.end_time ? ` – ${fmt(b.end_time)}` : ""}
                            </span>
                          )}
                          {b.event_type && <span className="cap">{b.event_type}</span>}
                          {b.venue_name && <span>{b.venue_name}</span>}
                          {b.guest_count && <span>{b.guest_count} guests</span>}
                        </div>
                      </div>
                      {!b._bufferType && (
                        <span
                          className="ec-status-dot"
                          style={{ background: STATUS_COLORS[b.status] || "var(--accent)" }}
                        />
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })()}

        {/* Calendar grid */}
        <div className={`ec-grid ${view === "week" ? "ec-grid-week" : ""}`}>
          {/* Weekday headers */}
          {WEEKDAYS.map((wd) => (
            <div key={wd} className="ec-weekday">
              {wd}
            </div>
          ))}

          {/* Day cells */}
          {gridDays.map((day) => {
            const iso = isoDate(day);
            const isCurrentMonth =
              view === "month" ? day.getMonth() === range.month : true;
            const isToday = iso === todayStr;
            const isBlocked = blockedSet.has(iso);
            const dayEvents = byDate[iso] || [];
            const isSelected = selectedDay === iso;

            return (
              <div
                key={iso}
                className={[
                  "ec-cell",
                  !isCurrentMonth && "ec-cell-outside",
                  isToday && "ec-cell-today",
                  isBlocked && "ec-cell-blocked",
                  isSelected && "ec-cell-selected",
                ]
                  .filter(Boolean)
                  .join(" ")}
                onClick={() => setSelectedDay(iso)}
              >
                <div className="ec-cell-date">
                  {view === "week" && (
                    <span className="ec-cell-weekday">{WEEKDAYS[day.getDay()]} </span>
                  )}
                  <span className={isToday ? "ec-today-badge" : ""}>
                    {day.getDate()}
                  </span>
                </div>
                <div className="ec-cell-events">
                  {dayEvents.slice(0, 3).map((b, idx) => {
                    const isBuffer = !!b._bufferType;
                    const bufferLabel =
                      b._bufferType === "setup"
                        ? `Setup \u2014 ${b.title || b.customer_name}`
                        : b._bufferType === "teardown"
                          ? `Teardown \u2014 ${b.title || b.customer_name}`
                          : null;

                    return (
                      <div
                        key={`${b.id}-${b._bufferType || "main"}-${idx}`}
                        className={`ec-chip ${isBuffer ? "ec-chip-buffer" : ""}`}
                        style={{
                          borderLeftColor: isBuffer
                            ? "var(--text-sub)"
                            : STATUS_COLORS[b.status] || "var(--accent)",
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          setDetailBooking(b);
                          setLogisticsEditing(false);
                        }}
                        title={
                          bufferLabel ||
                          `${b.customer_name} \u2014 ${b.event_type || b.title}`
                        }
                      >
                        {isBuffer ? (
                          <span className="ec-chip-name ec-chip-buffer-label">
                            {bufferLabel}
                          </span>
                        ) : (
                          <>
                            <span className="ec-chip-name">
                              {b.customer_name}
                            </span>
                            <span className="ec-chip-type">
                              {b.event_type || b.title}
                            </span>
                          </>
                        )}
                      </div>
                    );
                  })}
                  {dayEvents.length > 3 && (
                    <div className="ec-chip-more">
                      +{dayEvents.length - 3} more
                    </div>
                  )}
                </div>
                {isBlocked && <div className="ec-blocked-badge">Blocked</div>}
              </div>
            );
          })}
        </div>

        {/* ── Day detail panel ── */}
        {selectedDay && (
          <div className="ec-panel">
            <div className="ec-panel-header">
              <h3>
                {new Date(selectedDay + "T12:00:00").toLocaleDateString(
                  undefined,
                  { weekday: "long", month: "long", day: "numeric" }
                )}
              </h3>
              <button
                className="ec-panel-close"
                onClick={() => setSelectedDay(null)}
              >
                &times;
              </button>
            </div>

            {dayBlocked ? (
              <div className="ec-panel-blocked">
                <span>
                  Date is blocked
                  {dayBlockInfo?.reason ? `: ${dayBlockInfo.reason}` : ""}
                </span>
                <button
                  className="ec-btn-sm danger"
                  onClick={() =>
                    dayBlockInfo && handleUnblock(dayBlockInfo.id)
                  }
                >
                  Unblock
                </button>
              </div>
            ) : (
              <button
                className="ec-btn-sm secondary"
                onClick={() => openBlockPrompt(selectedDay)}
                style={{ marginBottom: 12 }}
              >
                Block date(s)
              </button>
            )}

            {dayBookings.length === 0 ? (
              <p className="ec-empty">No events booked.</p>
            ) : (
              <div className="ec-panel-list">
                {dayBookings.map((b, idx) => (
                  <div
                    key={`${b.id}-${b._bufferType || "main"}-${idx}`}
                    className={`ec-panel-card ${b._bufferType ? "ec-panel-card-buffer" : ""}`}
                    onClick={() => {
                      setDetailBooking(b);
                      setLogisticsEditing(false);
                    }}
                  >
                    <div className="ec-panel-card-header">
                      <strong>
                        {b._bufferType
                          ? `${b._bufferType === "setup" ? "Setup" : "Teardown"} \u2014 ${b.customer_name}`
                          : b.customer_name}
                      </strong>
                      <span
                        className="ec-status-dot"
                        style={{
                          background:
                            STATUS_COLORS[b.status] || "var(--accent)",
                        }}
                      />
                    </div>
                    <div className="ec-panel-card-meta">
                      {!b._bufferType && (
                        <span>
                          {fmt(b.start_time)}
                          {b.end_time ? ` \u2013 ${fmt(b.end_time)}` : ""}
                        </span>
                      )}
                      {b.event_type && <span>{b.event_type}</span>}
                      {b.guest_count && <span>{b.guest_count} guests</span>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Block date modal ── */}
      {blockPrompt && (
        <div className="ec-overlay" onClick={() => setBlockPrompt(null)}>
          <div className="ec-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Block Date(s)</h3>
            <p className="ec-modal-sub">
              Block starting{" "}
              {new Date(blockPrompt.date + "T12:00:00").toLocaleDateString(
                undefined,
                { month: "long", day: "numeric", year: "numeric" }
              )}
            </p>
            <label className="ec-modal-field">
              <span>End Date (optional, for multi-day block)</span>
              <input
                type="date"
                value={blockEndDate}
                min={blockPrompt.date}
                onChange={(e) => setBlockEndDate(e.target.value)}
              />
            </label>
            <label className="ec-modal-field">
              <span>Reason</span>
              <input
                value={blockReason}
                onChange={(e) => setBlockReason(e.target.value)}
                placeholder="e.g. Venue maintenance, holiday..."
              />
            </label>
            <div className="ec-modal-actions">
              <button
                className="ec-btn secondary"
                onClick={() => setBlockPrompt(null)}
              >
                Cancel
              </button>
              <button className="ec-btn primary" onClick={handleBlock}>
                Block Date{blockEndDate && blockEndDate !== blockPrompt.date ? "s" : ""}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Booking detail modal ── */}
      {detailBooking && (
        <div
          className="ec-overlay"
          onClick={() => {
            setDetailBooking(null);
            setLogisticsEditing(false);
          }}
        >
          <div
            className="ec-modal ec-modal-wide"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="ec-modal-header">
              <h3>{detailBooking.title || "Booking Details"}</h3>
              <button
                className="ec-panel-close"
                onClick={() => {
                  setDetailBooking(null);
                  setLogisticsEditing(false);
                }}
              >
                &times;
              </button>
            </div>

            <div className="ec-detail-grid">
              {/* Customer */}
              <div className="ec-detail-section">
                <div className="ec-detail-label">Customer</div>
                <div className="ec-detail-value">
                  {detailBooking.customer_name}
                  {detailBooking.customer_phone && (
                    <span className="ec-detail-sub">
                      {" "}
                      &middot; {detailBooking.customer_phone}
                    </span>
                  )}
                </div>
                {detailBooking.customer_id && (
                  <button
                    className="ec-btn-sm secondary"
                    onClick={() => {
                      setDetailBooking(null);
                      setLogisticsEditing(false);
                      navigate(`/customers/${detailBooking.customer_id}`);
                    }}
                    style={{ marginTop: 6 }}
                  >
                    View Customer
                  </button>
                )}
              </div>

              {/* Date & Time */}
              <div className="ec-detail-section">
                <div className="ec-detail-label">Date & Time</div>
                <div className="ec-detail-value">
                  {new Date(
                    detailBooking.date + "T12:00:00"
                  ).toLocaleDateString(undefined, {
                    weekday: "short",
                    month: "long",
                    day: "numeric",
                  })}{" "}
                  {fmt(detailBooking.start_time)}
                  {detailBooking.end_time
                    ? ` \u2013 ${fmt(detailBooking.end_time)}`
                    : ""}
                </div>
              </div>

              {/* Status */}
              <div className="ec-detail-section">
                <div className="ec-detail-label">Status</div>
                <div
                  className="ec-detail-value"
                  style={{ textTransform: "capitalize" }}
                >
                  <span
                    className="ec-status-dot"
                    style={{
                      background: STATUS_COLORS[detailBooking.status],
                    }}
                  />
                  {detailBooking.status}
                </div>
              </div>

              {/* Event Type */}
              {detailBooking.event_type && (
                <div className="ec-detail-section">
                  <div className="ec-detail-label">Event Type</div>
                  <div
                    className="ec-detail-value"
                    style={{ textTransform: "capitalize" }}
                  >
                    {detailBooking.event_type}
                  </div>
                </div>
              )}

              {/* Guest Count */}
              {detailBooking.guest_count && (
                <div className="ec-detail-section">
                  <div className="ec-detail-label">Guest Count</div>
                  <div className="ec-detail-value">
                    {detailBooking.guest_count}
                  </div>
                </div>
              )}

              {/* Venue */}
              {detailBooking.venue_name && (
                <div className="ec-detail-section">
                  <div className="ec-detail-label">Venue</div>
                  <div className="ec-detail-value">
                    {detailBooking.venue_name}
                  </div>
                </div>
              )}

              {/* Coordinator */}
              {detailBooking.assigned_to && (
                <div className="ec-detail-section">
                  <div className="ec-detail-label">Coordinator</div>
                  <div className="ec-detail-value">
                    {detailBooking.assigned_to}
                  </div>
                </div>
              )}

              {/* Quote */}
              {detailBooking.quote_number && (
                <div className="ec-detail-section">
                  <div className="ec-detail-label">Quote</div>
                  <div className="ec-detail-value">
                    {detailBooking.quote_number}
                    {detailBooking.quote_id && (
                      <button
                        className="ec-btn-sm secondary"
                        onClick={() => {
                          setDetailBooking(null);
                          setLogisticsEditing(false);
                          navigate(`/quotes/${detailBooking.quote_id}`);
                        }}
                        style={{ marginLeft: 8 }}
                      >
                        View Quote
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* Notes */}
              {detailBooking.notes && (
                <div className="ec-detail-section ec-detail-full">
                  <div className="ec-detail-label">Notes</div>
                  <div className="ec-detail-value">{detailBooking.notes}</div>
                </div>
              )}

              {/* ── Payment section ── */}
              <div className="ec-detail-section ec-detail-full">
                <div className="ec-detail-label">Payment</div>
                <div className="ec-payment-grid">
                  <div className="ec-payment-row">
                    <span className="ec-payment-label">Deposit</span>
                    <span className="ec-payment-amount">
                      {currency(detailBooking.deposit_amount)}
                    </span>
                    {detailBooking.deposit_paid ? (
                      <span className="ec-paid-tag">Paid</span>
                    ) : (
                      <button
                        className="ec-btn-sm primary"
                        onClick={() =>
                          detailBooking.quote_id &&
                          markDepositPaid(detailBooking.quote_id)
                        }
                        disabled={!detailBooking.quote_id}
                      >
                        Mark Paid
                      </button>
                    )}
                  </div>
                  <div className="ec-payment-row">
                    <span className="ec-payment-label">Balance</span>
                    <span className="ec-payment-amount">
                      {currency(detailBooking.balance_due)}
                    </span>
                    {detailBooking.balance_paid ? (
                      <span className="ec-paid-tag">Paid</span>
                    ) : (
                      <button
                        className="ec-btn-sm primary"
                        onClick={() =>
                          detailBooking.quote_id &&
                          markBalancePaid(detailBooking.quote_id)
                        }
                        disabled={!detailBooking.quote_id}
                      >
                        Mark Paid
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* ── Guest List section ── */}
              <div className="ec-detail-section ec-detail-full">
                <div className="ec-guest-summary">
                  <div className="ec-guest-summary-left">
                    <span className="ec-detail-label">Guest List</span>
                    {guestSummary && (
                      <span className="ec-guest-counts">
                        {guestSummary.confirmed || 0} confirmed
                        {" \u00B7 "}
                        {guestSummary.invited || 0} invited
                        {" \u00B7 "}
                        ~{guestSummary.total_with_plus_ones || guestSummary.total || 0} w/ plus-ones
                      </span>
                    )}
                  </div>
                  <div className="ec-guest-summary-right">
                    <label className="ec-waiver-toggle">
                      <span className="ec-waiver-toggle-label">Waivers required</span>
                      <input
                        type="checkbox"
                        checked={waiversRequired}
                        onChange={toggleWaivers}
                      />
                      <span className="ec-waiver-toggle-track" />
                    </label>
                  </div>
                </div>

                {guestsLoading ? (
                  <p className="ec-empty">Loading guests...</p>
                ) : (
                  <>
                    {guests.length === 0 ? (
                      <p className="ec-empty">No guests added yet.</p>
                    ) : (
                      <div className="ec-guest-list">
                        {guests.map((g) => (
                          <div key={g.id} className="ec-guest-row">
                            <div className="ec-guest-info">
                              <span className="ec-guest-name">{g.name}</span>
                              <span className="ec-guest-contact">
                                {[g.phone, g.email].filter(Boolean).join(" \u00B7 ")}
                              </span>
                            </div>

                            <div className="ec-guest-chips">
                              <button
                                className={`ec-rsvp-chip ec-rsvp-${g.rsvp_status}`}
                                onClick={() => cycleRsvp(g)}
                                title="Click to change RSVP status"
                              >
                                {g.rsvp_status}
                              </button>

                              {g.plus_ones > 0 && (
                                <span className="ec-plus-ones-badge">+{g.plus_ones}</span>
                              )}

                              {g.waiver_status === "signed" ? (
                                <span className="ec-waiver-chip ec-waiver-signed">Signed</span>
                              ) : g.waiver_status === "pending" ? (
                                <button
                                  className="ec-waiver-chip ec-waiver-pending"
                                  onClick={() => setWaiverModal(g)}
                                  title="Click to send or copy waiver link"
                                >
                                  Pending
                                </button>
                              ) : (
                                <span className="ec-waiver-chip ec-waiver-na">Not Required</span>
                              )}
                            </div>

                            <div className="ec-guest-actions">
                              {g.promoted_lead_id ? (
                                <span className="ec-lead-chip">Lead</span>
                              ) : (
                                <button
                                  className="ec-btn-sm secondary"
                                  onClick={() => promoteGuest(g)}
                                  title="Promote to lead"
                                >
                                  Promote
                                </button>
                              )}
                              <button
                                className="ec-btn-sm danger"
                                onClick={() => deleteGuest(g.id)}
                                title="Remove guest"
                              >
                                &times;
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Add single guest */}
                    {showAddGuest ? (
                      <div className="ec-add-guest-form">
                        <div className="ec-add-guest-fields">
                          <input
                            placeholder="Name *"
                            value={newGuestName}
                            onChange={(e) => setNewGuestName(e.target.value)}
                          />
                          <input
                            placeholder="Phone"
                            value={newGuestPhone}
                            onChange={(e) => setNewGuestPhone(e.target.value)}
                          />
                          <input
                            placeholder="Email"
                            value={newGuestEmail}
                            onChange={(e) => setNewGuestEmail(e.target.value)}
                          />
                        </div>
                        <div className="ec-add-guest-actions">
                          <button className="ec-btn-sm primary" onClick={addGuest}>
                            Add
                          </button>
                          <button
                            className="ec-btn-sm secondary"
                            onClick={() => {
                              setShowAddGuest(false);
                              setNewGuestName("");
                              setNewGuestPhone("");
                              setNewGuestEmail("");
                            }}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="ec-guest-add-buttons">
                        <button
                          className="ec-btn-sm secondary"
                          onClick={() => setShowAddGuest(true)}
                        >
                          + Add Guest
                        </button>
                        <button
                          className="ec-btn-sm secondary"
                          onClick={() => setShowBulkAdd((v) => !v)}
                        >
                          {showBulkAdd ? "Hide Bulk Add" : "Bulk Add"}
                        </button>
                      </div>
                    )}

                    {/* Bulk add */}
                    {showBulkAdd && (
                      <div className="ec-bulk-add">
                        <textarea
                          className="ec-bulk-textarea"
                          value={bulkText}
                          onChange={(e) => setBulkText(e.target.value)}
                          placeholder={"Name, phone, email\nJane Doe, 555-1234, jane@example.com\nJohn Smith, 555-5678"}
                          rows={4}
                        />
                        <button className="ec-btn-sm primary" onClick={bulkAddGuests}>
                          Add All
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* ── Waiver link modal ── */}
              {waiverModal && (
                <div className="ec-waiver-modal-backdrop" onClick={() => setWaiverModal(null)}>
                  <div className="ec-waiver-modal" onClick={(e) => e.stopPropagation()}>
                    <h4>Waiver for {waiverModal.name}</h4>
                    <div className="ec-waiver-url">
                      {`https://os.abon.ai/events/waiver/${waiverModal.waiver_token}/`}
                    </div>
                    <div className="ec-waiver-modal-actions">
                      <button
                        className="ec-btn-sm primary"
                        onClick={() => copyWaiverLink(waiverModal.waiver_token)}
                      >
                        Copy Link
                      </button>
                      {waiverModal.email && (
                        <button
                          className="ec-btn-sm secondary"
                          onClick={() => sendWaiver(waiverModal)}
                        >
                          Send Email
                        </button>
                      )}
                      <button
                        className="ec-btn-sm secondary"
                        onClick={() => setWaiverModal(null)}
                      >
                        Close
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* ── Logistics section ── */}
              <div className="ec-detail-section ec-detail-full">
                <div className="ec-detail-label-row">
                  <span className="ec-detail-label">Logistics</span>
                  {!logisticsEditing && (
                    <button
                      className="ec-btn-sm secondary"
                      onClick={() => openLogisticsEditor(detailBooking)}
                    >
                      Edit
                    </button>
                  )}
                </div>

                {logisticsEditing && logisticsForm ? (
                  <div className="ec-logistics-form">
                    <div className="ec-logistics-row">
                      <label className="ec-modal-field">
                        <span>Estimated Headcount</span>
                        <input
                          type="number"
                          value={logisticsForm.estimated_headcount}
                          onChange={(e) =>
                            handleLogisticsChange(
                              "estimated_headcount",
                              e.target.value
                            )
                          }
                          placeholder="0"
                        />
                      </label>
                      <label className="ec-modal-field">
                        <span>Final Headcount</span>
                        <input
                          type="number"
                          value={logisticsForm.final_headcount}
                          onChange={(e) =>
                            handleLogisticsChange(
                              "final_headcount",
                              e.target.value
                            )
                          }
                          placeholder="0"
                        />
                      </label>
                    </div>

                    <label className="ec-modal-field">
                      <span>Setup Notes</span>
                      <textarea
                        className="ec-textarea"
                        value={logisticsForm.setup_notes}
                        onChange={(e) =>
                          handleLogisticsChange("setup_notes", e.target.value)
                        }
                        rows={3}
                        placeholder="Setup instructions..."
                      />
                    </label>

                    {/* Vendors */}
                    <div className="ec-logistics-vendors">
                      <div className="ec-detail-label-row">
                        <span className="ec-detail-label">Vendors</span>
                        <button
                          className="ec-btn-sm secondary"
                          onClick={addVendor}
                          type="button"
                        >
                          + Add
                        </button>
                      </div>
                      {logisticsForm.vendors.length === 0 && (
                        <p className="ec-empty">No vendors added.</p>
                      )}
                      {logisticsForm.vendors.map((v, i) => (
                        <div key={i} className="ec-vendor-row">
                          <input
                            placeholder="Vendor name"
                            value={v.name}
                            onChange={(e) =>
                              handleVendorChange(i, "name", e.target.value)
                            }
                          />
                          <input
                            placeholder="Arrival time"
                            value={v.arrival_time}
                            onChange={(e) =>
                              handleVendorChange(
                                i,
                                "arrival_time",
                                e.target.value
                              )
                            }
                          />
                          <input
                            placeholder="Contact"
                            value={v.contact}
                            onChange={(e) =>
                              handleVendorChange(i, "contact", e.target.value)
                            }
                          />
                          <button
                            className="ec-btn-sm danger"
                            onClick={() => removeVendor(i)}
                            type="button"
                          >
                            &times;
                          </button>
                        </div>
                      ))}
                    </div>

                    {/* Day-of contact */}
                    <div className="ec-logistics-row">
                      <label className="ec-modal-field">
                        <span>Day-of Contact Name</span>
                        <input
                          value={logisticsForm.day_of_contact.name}
                          onChange={(e) =>
                            handleDayOfContactChange("name", e.target.value)
                          }
                          placeholder="Contact name"
                        />
                      </label>
                      <label className="ec-modal-field">
                        <span>Day-of Contact Phone</span>
                        <input
                          value={logisticsForm.day_of_contact.phone}
                          onChange={(e) =>
                            handleDayOfContactChange("phone", e.target.value)
                          }
                          placeholder="Phone number"
                        />
                      </label>
                    </div>

                    <label className="ec-modal-field">
                      <span>Additional Notes</span>
                      <textarea
                        className="ec-textarea"
                        value={logisticsForm.notes}
                        onChange={(e) =>
                          handleLogisticsChange("notes", e.target.value)
                        }
                        rows={2}
                        placeholder="Any other notes..."
                      />
                    </label>

                    <div className="ec-modal-actions">
                      <button
                        className="ec-btn secondary"
                        onClick={() => setLogisticsEditing(false)}
                      >
                        Cancel
                      </button>
                      <button
                        className="ec-btn primary"
                        onClick={saveLogistics}
                        disabled={logisticsSaving}
                      >
                        {logisticsSaving ? "Saving..." : "Save Logistics"}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="ec-logistics-view">
                    {detailBooking.logistics ? (
                      <>
                        {(detailBooking.logistics.estimated_headcount ||
                          detailBooking.logistics.final_headcount) && (
                          <div className="ec-logistics-meta">
                            {detailBooking.logistics.estimated_headcount && (
                              <span>
                                Est. headcount:{" "}
                                {detailBooking.logistics.estimated_headcount}
                              </span>
                            )}
                            {detailBooking.logistics.final_headcount && (
                              <span>
                                Final headcount:{" "}
                                {detailBooking.logistics.final_headcount}
                              </span>
                            )}
                          </div>
                        )}
                        {detailBooking.logistics.setup_notes && (
                          <div className="ec-logistics-meta">
                            <span className="ec-detail-sub">Setup notes:</span>{" "}
                            {detailBooking.logistics.setup_notes}
                          </div>
                        )}
                        {detailBooking.logistics.vendors &&
                          detailBooking.logistics.vendors.length > 0 && (
                            <div className="ec-logistics-meta">
                              <span className="ec-detail-sub">Vendors:</span>
                              {detailBooking.logistics.vendors.map((v, i) => (
                                <span key={i} className="ec-vendor-tag">
                                  {v.name}
                                  {v.arrival_time
                                    ? ` (${v.arrival_time})`
                                    : ""}
                                  {v.contact ? ` - ${v.contact}` : ""}
                                </span>
                              ))}
                            </div>
                          )}
                        {detailBooking.logistics.day_of_contact &&
                          (detailBooking.logistics.day_of_contact.name ||
                            detailBooking.logistics.day_of_contact.phone) && (
                            <div className="ec-logistics-meta">
                              <span className="ec-detail-sub">
                                Day-of contact:
                              </span>{" "}
                              {detailBooking.logistics.day_of_contact.name}
                              {detailBooking.logistics.day_of_contact.phone
                                ? ` (${detailBooking.logistics.day_of_contact.phone})`
                                : ""}
                            </div>
                          )}
                        {detailBooking.logistics.notes && (
                          <div className="ec-logistics-meta">
                            <span className="ec-detail-sub">Notes:</span>{" "}
                            {detailBooking.logistics.notes}
                          </div>
                        )}
                        {!detailBooking.logistics.estimated_headcount &&
                          !detailBooking.logistics.final_headcount &&
                          !detailBooking.logistics.setup_notes &&
                          (!detailBooking.logistics.vendors ||
                            detailBooking.logistics.vendors.length === 0) &&
                          !detailBooking.logistics.notes &&
                          !(
                            detailBooking.logistics.day_of_contact?.name ||
                            detailBooking.logistics.day_of_contact?.phone
                          ) && (
                            <p className="ec-empty">
                              No logistics details yet.
                            </p>
                          )}
                      </>
                    ) : (
                      <p className="ec-empty">No logistics details yet.</p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {loading && <div className="ec-loading">Loading calendar...</div>}
    </div>
  );
}
